import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Heart, Users, Shield, TrendingUp, MessageCircle, Clock, CheckCircle, ArrowRight, BarChart3, Lightbulb, UserCheck, Smile } from 'lucide-react';
import { Link } from 'react-router-dom';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';
import { HolographicCard } from '@/components/cards/HolographicCard';

const WorkforceOptimization = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  
  const workforceFeatures = [
    {
      icon: MessageCircle,
      title: "Compassionate Check-Ins",
      description: "Natural conversations that show your team you care, offering support resources in real time.",
      number: "01"
    },
    {
      icon: BarChart3,
      title: "Insightful Dashboards",
      description: "Intuitive HR and EAP integrations visualize well-being trends, enabling proactive interventions.",
      number: "02"
    },
    {
      icon: Lightbulb,
      title: "Continuous Learning",
      description: "AI analyzes workplace sentiment, identifying teams at risk and recommending wellness initiatives.",
      number: "03"
    },
    {
      icon: Shield,
      title: "Stress Detection",
      description: "Voice-first check-ins that sense fatigue, stress, and cognitive strain before burnout sets in.",
      number: "04"
    },
    {
      icon: UserCheck,
      title: "Proactive Support",
      description: "Early intervention systems that provide targeted wellness resources when teams need them most.",
      number: "05"
    }
  ];

  const customerFeatures = [
    {
      icon: Clock,
      title: "Instant Resolutions",
      description: "AI answers 70%+ of routine inquiries in seconds—no hold music, no frustration.",
      gradient: "from-primary/20 to-accent/20"
    },
    {
      icon: TrendingUp,
      title: "Smart Escalations",
      description: "Complex concerns are intelligently routed to the right expert, preserving empathy in every transfer.",
      gradient: "from-accent/20 to-secondary/20"
    },
    {
      icon: Heart,
      title: "Adaptive Engagement",
      description: "Conversations evolve with patient mood and history, building trust and loyalty over time.",
      gradient: "from-secondary/20 to-primary/20"
    }
  ];

  const impactMetrics = [
    {
      icon: TrendingUp,
      title: "25% Reduction in Turnover",
      description: "Organizations using Scienza's workforce AI see significant drops in staff burnout and turnover rates.",
      gradient: "from-primary/20 to-accent/20"
    },
    {
      icon: Smile,
      title: "85% Patient Satisfaction",
      description: "Patients report feeling more supported and heard in AI-powered interactions.",
      gradient: "from-accent/20 to-primary/20"
    }
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < workforceFeatures.length - 1) {
      setTimeout(() => {
        setTypingCardIndex(index + 1);
      }, 300);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTypingCardIndex(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Workforce & Customer Experience Optimization - Empower Your Team | Scienza Health</title>
        <meta 
          name="description" 
          content="Empower your staff with AI solutions that lift the burden of routine tasks while delivering exceptional customer experiences that build trust and loyalty." 
        />
      </Helmet>

      <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-depth)' }}>
        <NeuralBackground />
        
        {/* Hero Section */}
        <section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full mb-8">
                <Heart className="w-12 h-12 text-primary" />
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                Workforce Optimization
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                  Customer Experience
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-10">
                Your team is the heart of care—when they flourish, patients thrive. Scienza's AI solutions lift the burden of routine tasks, so caregivers feel empowered, supported, and inspired every day.
              </p>

              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart className="w-5 h-5 mr-3" />
                Transform Your Workforce
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Workforce Optimization Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="Workforce Optimization" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Empower your staff with voice-first check-ins that sense fatigue, stress, and cognitive strain—before burnout sets in."
                delay={1200}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {workforceFeatures.map((feature, index) => (
                <JourneyCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  number={feature.number}
                  index={index}
                  shouldStartTyping={index === typingCardIndex}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Customer Experience Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="Customer Experience" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Delight patients with seamless, human-like interactions that feel tailored and reassuring."
                delay={800}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {customerFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                >
                  <HolographicCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    index={index}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="Measurable Impact" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Real results from organizations transforming their workforce and customer experience"
                delay={800}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
              {impactMetrics.map((metric, index) => (
                <motion.div
                  key={metric.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                >
                  <FloatingIslandCard
                    icon={metric.icon}
                    title={metric.title}
                    description={metric.description}
                    gradient={metric.gradient}
                    index={index}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Empower Your Team Today
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto">
                Transform your workforce and customer experience with AI solutions that understand, support, and elevate human potential. 
                When your team flourishes, exceptional care follows.
              </p>
              
              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Users className="w-5 h-5 mr-3" />
                Start Your Transformation
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WorkforceOptimization;